import {useEffect, useRef, type CSSProperties, type ReactNode} from 'react';
import {X} from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {Button} from '@/components/app/button';
import {cn} from '@/lib/utils';

/** PrimeReact dialog title: 1.25rem, medium weight. Shared with confirmations. */
export const dialogTitleClassName = 'm-0 text-xl leading-tight font-medium';

export interface ModalProps {
  open?: boolean;
  onClose?: () => void;
  onShow?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  style?: CSSProperties;
  contentClassName?: string;
  contentStyle?: CSSProperties;
  headerClassName?: string;
  headerStyle?: CSSProperties;
  showHeader?: boolean;
  closable?: boolean;
  closeOnEscape?: boolean;
  dismissOnOutsideClick?: boolean;
  modal?: boolean;
}

export function Modal({
  open = false,
  onClose,
  onShow,
  title,
  description,
  children,
  footer,
  className,
  style,
  contentClassName,
  contentStyle,
  headerClassName,
  headerStyle,
  showHeader = true,
  closable = true,
  closeOnEscape = true,
  dismissOnOutsideClick = false,
  modal = true,
}: ModalProps) {
  const origin = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current)
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  wasOpen.current = open;
  useEffect(() => {
    if (open) onShow?.();
  }, [open]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose?.();
      }}
      modal={modal}
    >
      <DialogContent
        // Like PrimeReact dialogs: as wide as the content or the width given via className/style, within the viewport.
        className={cn(
          'flex max-h-[calc(100dvh-2rem)] w-fit max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[calc(100%-2rem)]',
          className,
        )}
        style={style}
        // Like PrimeReact, the close button sits in the header row: no header, no close button.
        showCloseButton={false}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (origin.current?.isConnected) origin.current.focus();
        }}
        onEscapeKeyDown={(event) => {
          if (!closeOnEscape) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (!dismissOnOutsideClick) event.preventDefault();
        }}
      >
        <DialogHeader
          className={cn(
            showHeader ? 'shrink-0 gap-2 p-6 text-left' : 'sr-only',
            showHeader && closable && 'pr-18',
            headerClassName,
          )}
          style={headerStyle}
        >
          {/* As tall as the close button, so the title stays centered on it. Untitled dialogs show only the button. */}
          <DialogTitle
            className={cn(dialogTitleClassName, closable && 'flex min-h-10 items-center', title == null && 'sr-only')}
          >
            {title ?? 'Details'}
          </DialogTitle>
          <DialogDescription className={description ? undefined : 'sr-only'}>
            {description ?? 'Review and update the details in this dialog.'}
          </DialogDescription>
        </DialogHeader>
        {/* The header's padding already separates it from the content, so the body has no top padding. */}
        <div
          data-slot="modal-body"
          className={cn('min-h-0 overflow-auto px-6 pb-6', !showHeader && 'pt-6', contentClassName)}
          style={contentStyle}
        >
          {children}
        </div>
        {footer && <DialogFooter className="shrink-0 px-6 py-4">{footer}</DialogFooter>}
        {/* Placed in the header row like PrimeReact's, but after the content so opening the dialog focuses the
            first field rather than this button. */}
        {closable && showHeader && (
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon"
              data-modal-close
              className="absolute top-6 right-6 size-10 text-muted-foreground"
            >
              <X />
              <span className="sr-only">Close</span>
            </Button>
          </DialogClose>
        )}
      </DialogContent>
    </Dialog>
  );
}
